import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';
import {classify} from 'src/utils/classify';

import {Smallest} from 'src/designSystem2021Components/text-v2';

import css from './input.css';


export type Props = {
  value?: string | number;
  onChange?: (
    evt: React.ChangeEvent<HTMLInputElement>,
    isEnter?: boolean,
  ) => unknown;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => unknown;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => unknown;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => unknown;
  onPaste?: (e: React.ClipboardEvent<Element>) => unknown;
  name?: string;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  errorText?: string | null | undefined;
  label?: string | React.ReactNode;
  contextLabel?: string | React.ReactNode;
  className?: string;
  boxClassName?: string;
  type?: 'text' | 'number' | 'password' | 'email' | 'tel' | 'url';
  clearable?: boolean;
};

const Input_ = (
  props: Props &
    Omit<
      Omit<
        React.HTMLProps<HTMLInputElement>,
        | 'disabled'
        | 'name'
        | 'ref'
        | 'placeholder'
        | 'value'
        | 'onChange'
        | 'onFocus'
        | 'onBlur'
      >,
      keyof Props
    >,
  ref:
    | ((arg1: null | HTMLInputElement) => unknown)
    | {
        current: null | HTMLInputElement;
      },
) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    name,
    disabled,
    placeholder,
    error,
    errorText,
    label,
    contextLabel,
    className,
    boxClassName,
    clearable,
    ...inputProps
  } = props;

  //Note(Aditya): we need to know filled so we can suppress error state when the user is trying to fix it.
  // getting value from uncontrolled is complicated and unnecessary since error states only work with controlled inputs.
  const controlledInputFilled = value !== '';

  return (
    <div
      className={classify(css.className, css.wrapper, {
        [css.filled]: controlledInputFilled ?? false,
        [css.withError]: error ?? false,
      })}
    >
      {(Boolean(label) || Boolean(contextLabel) || error) && (
        <div className={css.info}>
          <Smallest className={css.commonLabel}>{label ?? ''}</Smallest>
          <Smallest
            className={error && errorText ? css.errorLabel : css.commonLabel}
          >
            {error && errorText ? errorText : contextLabel ?? ''}
          </Smallest>
        </div>
      )}
      <div
        className={classify(css.box, boxClassName, {
          [css.inputDisabled]: disabled ?? false,
        })}
      >
        <input
          disabled={disabled}
          name={name}
          // @ts-expect-error - TS2322 - Type '{ current: HTMLInputElement | null; } | ((arg1: HTMLInputElement | null) => unknown)' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
          ref={ref}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          {...inputProps}
        />
      </div>
    </div>
  );
};

// @ts-expect-error - TS2352 - Conversion of type 'ForwardRefExoticComponent<HTMLInputElement & RefAttributes<Props>>' to type 'AbstractComponent<Props, HTMLInputElement>' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
export const Input = React.forwardRef<Props, HTMLInputElement>(
  // @ts-expect-error - TS2345 - Argument of type '(props: Props & Omit<Omit<React.HTMLProps<HTMLInputElement>, "disabled" | "name" | "ref" | "placeholder" | "value" | "onChange" | "onFocus" | "onBlur">, keyof Props>, ref: ((arg1: null | HTMLInputElement) => unknown) | { current: null | HTMLInputElement; }) => React.JSX.Element' is not assignable to parameter of type 'ForwardRefRenderFunction<Props, HTMLInputElement>'.
  Input_,
) as Flow_AbstractComponent<Props, HTMLInputElement>;
